import { formatPercent } from '../../charts/formatters'
import { normalizePlatformKey } from '../../../lib/utils/socialNetworkCatalog'

export const OBJECTIVE_DEFINITIONS = [
  { id: 'awareness', name: 'Awareness', primaryKey: 'impressions', primaryLabel: 'Impressions', isPercent: false },
  { id: 'traffic', name: 'Traffic', primaryKey: 'linkClicks', primaryLabel: 'Link Clicks', isPercent: false },
  { id: 'sales', name: 'Sales', primaryKey: 'purchases', primaryLabel: 'Purchases', isPercent: false },
  { id: 'leads', name: 'Leads', primaryKey: 'leads', primaryLabel: 'Leads', isPercent: false },
  { id: 'engagement', name: 'Engagement', primaryKey: 'engagementRate', primaryLabel: 'Eng. Rate', isPercent: true },
]

export const CAMPAIGN_NAMES_BY_OBJECTIVE = {
  awareness: ['Brand Awareness Push', 'Reach Expansion', 'Video Views Drive'],
  traffic: ['Website Traffic Drive', 'Landing Page Push', 'Blog Traffic'],
  sales: ['Reservation Campaign', 'Flash Sale Promo', 'Booking Conversion'],
  leads: ['Lead Gen Form', 'Newsletter Signup', 'Webinar Registration'],
  engagement: ['Community Boost', 'Content Engagement', 'Social Interaction'],
}

export const ADS_SORT_OPTIONS = ['roas', 'revenue', 'spend']

/** Meta ad platforms shown on Paid Media Performance (All + IG/FB). */
export const PAID_MEDIA_AD_PLATFORM_OPTIONS = ['instagram', 'facebook']

export const TABLE_COLUMNS = [
  { key: 'objective', label: 'Objective', sortType: 'string' },
  { key: 'campaignName', label: 'Campaign Name', sortType: 'string' },
  { key: 'platform', label: 'Platform', sortType: 'string' },
  { key: 'format', label: 'Format', sortType: 'string' },
  { key: 'impressions', label: 'Impressions', sortType: 'number' },
  { key: 'reach', label: 'Reach', sortType: 'number' },
  { key: 'linkClicks', label: 'Link Clicks', sortType: 'number' },
  { key: 'cpc', label: 'CPC', sortType: 'number', headerTooltip: 'Cost per click' },
  { key: 'postEngagement', label: 'Post Engagement', sortType: 'number' },
  { key: 'cpe', label: 'CPE', sortType: 'number', headerTooltip: 'Cost per engagement' },
  { key: 'engagementRate', label: 'Engagement Rate', sortType: 'number' },
  { key: 'newFollowers', label: 'New Followers', sortType: 'number' },
  { key: 'shares', label: 'Shares', sortType: 'number' },
  { key: 'saves', label: 'Saves', sortType: 'number' },
  { key: 'videoViews', label: 'Video Views', sortType: 'number' },
  { key: 'spend', label: 'Spend', sortType: 'number' },
  { key: 'roas', label: 'ROAS', sortType: 'number' },
  { key: 'revenue', label: 'Revenue', sortType: 'number' },
  { key: 'purchases', label: 'Purchases', sortType: 'number' },
]

function formatCostPerResult(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '$0.00'
  return `$${n.toFixed(2)}`
}

function hashSeed(str) {
  let h = 0
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

export function enrichTopAd(ad) {
  const seed = hashSeed(ad.id)
  const objectiveDef = OBJECTIVE_DEFINITIONS[seed % OBJECTIVE_DEFINITIONS.length]
  const campaignOptions = CAMPAIGN_NAMES_BY_OBJECTIVE[objectiveDef.id] || ['Paid Campaign']
  const campaignIndex = hashSeed(`${ad.id}-campaign`) % campaignOptions.length
  const campaignName = campaignOptions[campaignIndex]
  const campaignId = `${objectiveDef.id}-${campaignIndex}`
  const impressions = Math.round(ad.spend * (8 + (seed % 20)))
  const reach = Math.round(impressions * (0.65 + (seed % 15) / 100))
  const linkClicks = Math.round(reach * (0.02 + (seed % 8) / 1000))
  const purchases = Math.max(0, Math.round(ad.revenue / (80 + (seed % 50))))
  const pe = Math.round(ad.spend * 0.3 + (seed % 100))
  const engagementRate = impressions ? pe / impressions : 0

  return {
    ...ad,
    objective: objectiveDef.name,
    objectiveId: objectiveDef.id,
    campaignId,
    campaignName,
    platformKey: normalizePlatformKey(ad.platform),
    impressions,
    reach,
    linkClicks,
    purchases,
    pe,
    engagementRate,
  }
}

export function buildCampaignSummaries(ads, objectiveFilter = null, campaigns = []) {
  const seenAdIds = new Set()
  let filtered = []
  for (const ad of ads || []) {
    const key = ad?.id != null ? String(ad.id) : ''
    if (key) {
      if (seenAdIds.has(key)) continue
      seenAdIds.add(key)
    }
    filtered.push(ad)
  }
  if (objectiveFilter) {
    filtered = filtered.filter((a) => a.objective === objectiveFilter)
  }

  const campaignMap = new Map()
  filtered.forEach((ad) => {
    if (!campaignMap.has(ad.campaignId)) {
      campaignMap.set(ad.campaignId, {
        id: ad.campaignId,
        name: ad.campaignName,
        objective: ad.objective,
        objectiveId: ad.objectiveId,
        ads: [],
      })
    }
    campaignMap.get(ad.campaignId).ads.push(ad)
  })

  const overlayById = new Map()
  for (const row of campaigns || []) {
    const id = String(row?.campaignId || row?.campaign_id || '')
    if (id && !overlayById.has(id)) overlayById.set(id, row)
  }

  return Array.from(campaignMap.values())
    .map((campaign) => {
      const overlay = overlayById.get(String(campaign.id))
      const sorted = [...campaign.ads].sort((a, b) => b.roas - a.roas)
      const spend = overlay?.spend ?? campaign.ads.reduce((sum, a) => sum + a.spend, 0)
      const revenue = overlay?.revenue ?? campaign.ads.reduce((sum, a) => sum + a.revenue, 0)
      const roas = overlay?.roas ?? (spend > 0 ? revenue / spend : 0)

      return {
        ...campaign,
        name: overlay?.campaignName || overlay?.name || campaign.name,
        adCount: campaign.ads.length,
        topAd: sorted[0],
        spend,
        revenue,
        roas,
      }
    })
    .sort((a, b) => b.roas - a.roas)
}

export function buildObjectiveSummaries(ads) {
  return OBJECTIVE_DEFINITIONS.map((def) => {
    const objectiveAds = ads.filter((a) => a.objectiveId === def.id)
    if (objectiveAds.length === 0) return null

    const campaigns = buildCampaignSummaries(objectiveAds)
    const topCampaign = campaigns[0]
    const spend = objectiveAds.reduce((sum, a) => sum + a.spend, 0)
    const revenue = objectiveAds.reduce((sum, a) => sum + a.revenue, 0)
    const roas = spend > 0 ? revenue / spend : 0

    return {
      ...def,
      campaignCount: campaigns.length,
      adCount: objectiveAds.length,
      topCampaign,
      spend,
      revenue,
      roas,
    }
  }).filter(Boolean)
}

export function compareValues(a, b, sortType) {
  if (sortType === 'string') {
    return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' })
  }
  return (Number(a) || 0) - (Number(b) || 0)
}

export function formatTableCell(ad, column, { formatCompactNumber, platformLabels }) {
  const key = column.key
  if (key === 'platform') {
    return platformLabels[ad.platformKey] || ad.platform || '—'
  }
  if (key === 'campaignName') {
    return ad.campaignName || ad.campaign_name || ad.title || '—'
  }
  const value = ad[key]
  if (value === null || value === undefined || value === '') {
    if (key === 'cpc' || key === 'cpe') return '$0.00'
    if (key === 'revenue' || key === 'spend') return '$0'
    if (key === 'roas') return '0.0x'
    if (key === 'engagementRate') return formatPercent(0, 1)
    if (
      ['purchases', 'impressions', 'reach', 'linkClicks', 'postEngagement', 'newFollowers', 'shares', 'saves', 'videoViews'].includes(
        key,
      )
    ) {
      return '0'
    }
    return '—'
  }

  if (key === 'roas') return `${Number(value).toFixed(1)}x`
  if (key === 'revenue' || key === 'spend') return `$${formatCompactNumber(value)}`
  if (key === 'cpc' || key === 'cpe') return formatCostPerResult(value)
  if (key === 'engagementRate') return formatPercent(value, 1)
  if (['impressions', 'reach', 'linkClicks', 'postEngagement', 'newFollowers', 'shares', 'saves', 'videoViews'].includes(key)) {
    return formatCompactNumber(value)
  }
  if (typeof value === 'number') return formatCompactNumber(value)
  return String(value)
}

export function filterAdsByPlatforms(ads, platforms) {
  const showAll = platforms?.has('all') || !platforms || platforms.size === 0
  if (showAll) return ads
  return ads.filter((ad) => platforms.has(ad.platformKey))
}

export function sortByMetric(items, sortBy, sortDir) {
  const list = Array.isArray(items) ? items : []
  const dir = sortDir === 'asc' ? 1 : -1

  const metricValue = (item) => {
    if (!item) return 0
    const raw = item[sortBy]
    const num = Number(raw)
    return Number.isFinite(num) ? num : 0
  }

  return [...list].sort((a, b) => {
    const cmp = metricValue(a) - metricValue(b)
    if (cmp !== 0) return cmp * dir
    const tie = String(a.id ?? a.name ?? '').localeCompare(String(b.id ?? b.name ?? ''))
    return tie * dir
  })
}
